import isEmpty from 'lodash/isEmpty';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ThemeV2 from '../../../../theme';
import { getTaskCategoryBasedOnDueDate } from '../../../../controllers/notebook/NotebookViewController/utils';
import useToggle from '../../../../hooks/useToggle';
import {
  Assignee,
  NotebookViews,
  TaskCategories,
} from '../../../../interfaces/notebook';
import {
  BOARD_TASK_CREATED_BY,
  LINK_COPY_SUCCESS_TOAST,
  NOTEBOOK_TASK_TITLE_PLACEHOLDER,
  TASK_CREATION_ERROR,
  TRY_AGAIN,
} from '../../../../languages/en/notebook';
import useNotebookStore from '../../../../stores/notebookStore';
import { NOTEBOOK_TASK_EVENTS } from '../../../../Utils/analytics/constants';
import SVGIcon from '../../../atoms/SVGIcon';
import SpinningLoader from '../../../atoms/SpinnerLoader';
import TaskItemDatePicker from '../../../molecules/Notebook/TaskItem/TaskItemDatePicker';
import TaskItemTextArea from '../../../molecules/Notebook/TaskItem/TaskItemTextArea';
import {
  BoardEditIconWrapper,
  EditorFooterText,
  EditorWrapper,
  ErrorText,
  BoardErrorWrapper,
  StyledDropdownIconButton,
  StyledTaskContent,
  StyledTiptapEditorWrapper,
  TaskItemWrapper,
  TryAgain,
  HoverActionContainer,
  Wrapper,
  LoaderWrapper,
  CreatedByName,
  CreatedByText,
} from './styles';
import { BoardTaskItemProps } from './types';
import { motion } from 'framer-motion';
// eslint-disable-next-line max-len
import { opacityAnimationProps } from '../../../../controllers/notebook/NotebookViewController/useNotebookViewController/data';
import { Flex } from '../../../../Utils/styles/display';
import { BoardTaskDropdownOperations } from '../../../../controllers/notebook/NotebookBoardTasksController/useNotebookBoardTasksController/data';
import { showSuccessMessage } from '../../../../Utils/toast';
import { getFullName } from '../../../../Utils/user';
import { isMobilePlatform } from '../../../../Utils/window';
import {
  convertStringToTokenizedObject,
  convertTokenizedObjectToString,
} from '../../../../Utils/notebook';
import TaskItemAssigneeSelector from '../TaskItemAssigneeSelector';
import { copyToClipboard } from '../../../../Utils/common';
import useMultiAssemblySlug from '../../../../hooks/useMultiAssemblySlug';
import useLayoutStore from '../../../../stores/layoutStore';

const BoardTaskItem = ({
  currentUserId,
  hasError,
  taskId,
  searchValue,
  assigneesList,
  hasMoreAssigneesToFetch,
  isFetchingMoreAssignees,
  currentUserTimezone,
  onEditorUpdate,
  updateTask,
  onTaskDataChange,
  onTaskClick,
  setTaskIdOfFocusedTask,
  fetchMoreAssignees,
  handleSearchOnAssigneeList,
  handleAssigneeChange,
  dropdownOptions,
  archiveTask,
  isLoading = false,
  handleCreatorLinkClick,
  markTasksAsRead,
}: BoardTaskItemProps) => {
  const task = useNotebookStore(
    useCallback((store) => store.allNotes[taskId], [taskId]),
  );

  const completedSectionId = useNotebookStore(
    useCallback((store) => store.completedSectionId || null, []),
  );

  const [render, setRender] = useState(false);

  const { slugUrl } = useMultiAssemblySlug();

  const { isEmbeddedInMainApp } = useLayoutStore();

  const {
    note,
    dueDate,
    isDeleted,
    stateEffectiveAt,
    assignedTo,
    noteId,
    type,
    isUnread,
    createdBy,
  } = task;

  const serializedTitle = useMemo(
    () => convertTokenizedObjectToString(note),
    [note],
  );

  useEffect(() => {
    setRender(true);
  }, []);

  useEffect(() => {
    onTaskDataChange({
      action: NOTEBOOK_TASK_EVENTS.NOTEBOOK_TASK_SHOW,
      originalTask: task,
      originalCategory: type,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dateBasedOnCategory = useMemo(() => {
    if (
      type === TaskCategories.COMPLETED ||
      type === TaskCategories.ARCHIVED ||
      isDeleted
    )
      return stateEffectiveAt;
    else return dueDate;
  }, [dueDate, isDeleted, stateEffectiveAt, type]);

  const {
    models: { toggleValue: isInEditMode },
    operations: {
      setToggleToFalse: setEditModeToFalse,
      setToggleToTrue: setEditModeToTrue,
    },
  } = useToggle(isEmpty(note));

  const handleTaskUpdate = useCallback(
    (content: string) => {
      if (content && content.trim().length > 0 && content !== serializedTitle) {
        onEditorUpdate({
          ...task,
          note: convertStringToTokenizedObject(content.trim()),
        });
      }
      setEditModeToFalse();
    },
    [onEditorUpdate, serializedTitle, setEditModeToFalse, task],
  );

  const handleDueDateChange = useCallback(
    (updatedDueDate: string | undefined) => {
      const updatedTask = { ...task, dueDate: updatedDueDate };
      const updatedCategory = getTaskCategoryBasedOnDueDate(
        updatedDueDate,
        currentUserTimezone,
      );
      updateTask(updatedTask, type, updatedCategory);
      onTaskDataChange({
        action: NOTEBOOK_TASK_EVENTS.NOTEBOOK_TASK_DUE_DATE_UPDATE,
        originalTask: task,
        originalCategory: type,
        updatedTask,
        updatedCategory,
      });
      setEditModeToFalse();
    },
    [
      setEditModeToFalse,
      task,
      type,
      currentUserTimezone,
      updateTask,
      onTaskDataChange,
    ],
  );

  const handleAssigneeSelection = useCallback(
    (assignee: Assignee) => {
      const updatedTask = { ...task, assignedTo: assignee };
      handleAssigneeChange(updatedTask, assignedTo, assignee);
    },
    [assignedTo, handleAssigneeChange, task],
  );

  const handleEditClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      setEditModeToTrue();
      e.stopPropagation();
    },
    [setEditModeToTrue],
  );

  const handleMenuItemClick = useCallback(
    (value: string | number) => {
      const operation = value as BoardTaskDropdownOperations;
      const copyUrl = isEmbeddedInMainApp
        ? `${window.location.origin}/a/${slugUrl}/notebook/${NotebookViews.Board}?taskId=${task.noteId}`
        : `${window.location.origin}/${slugUrl}/notebook/${NotebookViews.Board}?taskId=${task.noteId}`;
      switch (operation) {
        case BoardTaskDropdownOperations.EDIT_TASK_TITLE:
          setEditModeToTrue();
          break;

        case BoardTaskDropdownOperations.VIEW_TASK:
          if (task.noteId) {
            onTaskClick(task.noteId);
          }
          break;

        case BoardTaskDropdownOperations.COPY_LINK:
          showSuccessMessage(LINK_COPY_SUCCESS_TOAST);
          copyToClipboard(copyUrl);
          break;

        case BoardTaskDropdownOperations.COMPLETE:
          updateTask(
            {
              ...task,
              type: TaskCategories.COMPLETED,
              section: completedSectionId,
            },
            type,
            TaskCategories.COMPLETED,
          );
          break;

        case BoardTaskDropdownOperations.ARCHIVE:
          archiveTask({
            ...task,
            type: TaskCategories.ARCHIVED,
          });
          break;

        default:
          break;
      }
    },
    [
      isEmbeddedInMainApp,
      slugUrl,
      task,
      setEditModeToTrue,
      updateTask,
      completedSectionId,
      type,
      archiveTask,
      onTaskClick,
    ],
  );

  const renderEditor = () => {
    if (isInEditMode) {
      return (
        <StyledTiptapEditorWrapper id="add-new-task">
          <TaskItemTextArea
            serializedTitle={serializedTitle}
            onBlur={handleTaskUpdate}
            handleEnterClick={handleTaskUpdate}
            hasHoverEffect={false}
            placeholder={NOTEBOOK_TASK_TITLE_PLACEHOLDER}
          />
        </StyledTiptapEditorWrapper>
      );
    }
    return (
      <div>
        {!isMobilePlatform && (
          <HoverActionContainer inlineFlex>
            <BoardEditIconWrapper
              className={`${!isLoading && 'hover-icons'}`}
              onClick={handleEditClick}
            >
              <SVGIcon icon="edit" size="14px" color={ThemeV2.palette.gray8} />
            </BoardEditIconWrapper>
            <StyledDropdownIconButton
              menuItems={dropdownOptions || []}
              icon="more"
              title="more"
              onItemClick={handleMenuItemClick}
              iconColor={ThemeV2.palette.gray8}
              stopEventPropagation
              className={`${!isLoading && 'hover-icons'}`}
              iconBgColor={ThemeV2.palette.gray1}
              color="tertiary"
              isDropdownPositionFixed
            />
          </HoverActionContainer>
        )}

        <StyledTaskContent
          content={note}
          strikeThroughOverride={
            type === TaskCategories.COMPLETED ||
            type === TaskCategories.ARCHIVED
          }
          disabled={type === TaskCategories.ARCHIVED}
          noPadding
          hasHoverEffect={false}
        />
      </div>
    );
  };

  const animationStyleProps = useMemo(
    () =>
      !render
        ? {
            animation: `fadeOut 2s`,
          }
        : {},
    [render],
  );

  return (
    <Wrapper
      onMouseEnter={() => {
        if (isUnread) {
          markTasksAsRead(taskId, noteId, type);
        }
      }}
      isLoading={isLoading}
      style={animationStyleProps}
    >
      {isLoading && (
        <LoaderWrapper>
          <SpinningLoader scale={0.1} />
        </LoaderWrapper>
      )}
      <motion.div {...opacityAnimationProps}>
        <TaskItemWrapper
          highlight={false}
          data-testid={`task-${taskId}`}
          onClick={(e) => {
            if (e.defaultPrevented) {
              return;
            }
            if (task.noteId) {
              onTaskClick(task.noteId);
            }
          }}
          isUnread={isUnread}
          isBoardItem
        >
          <EditorWrapper>
            {renderEditor()}

            <EditorFooterText>
              {isUnread && (
                <CreatedByText inline variant="body3" color="gray7">
                  {`${BOARD_TASK_CREATED_BY}`}
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCreatorLinkClick({
                        memberId: createdBy?.memberID || '',
                        firstName: createdBy?.firstName,
                      });
                    }}
                  >
                    <CreatedByName
                      inline
                      variant="body3"
                      color="geekBlue6"
                    >{` ${getFullName(createdBy)}`}</CreatedByName>
                  </span>
                </CreatedByText>
              )}
            </EditorFooterText>
            <Flex justifyContent="space-between">
              <Flex>
                <TaskItemDatePicker
                  taskType={type}
                  dueDate={dateBasedOnCategory}
                  setDueDate={handleDueDateChange}
                  stopEventPropagation
                  isDeleted={false}
                  currentUserTimezone={currentUserTimezone}
                />
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    setTaskIdOfFocusedTask(noteId);
                  }}
                >
                  <TaskItemAssigneeSelector
                    assigneeList={assigneesList || []}
                    isLoading={isFetchingMoreAssignees}
                    onMemberClick={handleAssigneeSelection}
                    hasMoreUsersToFetch={hasMoreAssigneesToFetch}
                    canPopoverTriggerByClick
                    position
                    fetchMoreMembers={fetchMoreAssignees}
                    id={taskId}
                    assignedTo={assignedTo}
                    searchValue={searchValue}
                    handleSearchOnAssigneeList={handleSearchOnAssigneeList}
                    setTaskIdOfFocusedTask={setTaskIdOfFocusedTask}
                    currentUserId={currentUserId}
                    hideName
                  />
                </div>
              </Flex>
            </Flex>
            {hasError && (
              <BoardErrorWrapper>
                <SVGIcon
                  icon="warning-hollow"
                  size="16px"
                  color={ThemeV2.palette.dustRed6}
                />
                <ErrorText inline variant="body3" color="dustRed6">
                  {TASK_CREATION_ERROR}
                  <TryAgain
                    onClick={() => {
                      updateTask(task, type, type);
                    }}
                  >
                    {TRY_AGAIN}
                  </TryAgain>
                </ErrorText>
              </BoardErrorWrapper>
            )}
          </EditorWrapper>
        </TaskItemWrapper>
      </motion.div>
    </Wrapper>
  );
};

const MemoizedBoardTaskItem = React.memo(BoardTaskItem);
MemoizedBoardTaskItem.displayName = 'BoardTaskItem';

export default BoardTaskItem;
